import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface InstallPWAProps {
  variant?: "nav" | "compact" | "icon";
}

export default function InstallPWA({ variant = "nav" }: InstallPWAProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [platform, setPlatform] = useState<"ios" | "android" | "desktop">("desktop");

  useEffect(() => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) setPlatform("ios");
    else if (/Android/i.test(ua)) setPlatform("android");

    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      // @ts-ignore iOS Safari
      window.navigator.standalone === true;
    setIsInstalled(standalone);

    // Capture prompt globally so any button instance can use it
    const w = window as any;
    if (w.__tmInstallPrompt) setDeferredPrompt(w.__tmInstallPrompt);

    const handler = (e: Event) => {
      e.preventDefault();
      w.__tmInstallPrompt = e;
      setDeferredPrompt(e);
    };
    const installedHandler = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      w.__tmInstallPrompt = null;
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const handleInstallClick = async () => {
    const prompt = deferredPrompt || (window as any).__tmInstallPrompt;
    if (!prompt) {
      setShowHelp(true);
      return;
    }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
      (window as any).__tmInstallPrompt = null;
    }
  };

  if (isInstalled) return null;

  const icon = variant === "icon";
  const compact = variant === "compact";

  return (
    <>
      <button
        onClick={handleInstallClick}
        aria-label="Install TinyMinds App"
        title="Install TinyMinds App"
        className={`flex items-center gap-1.5 font-fredoka font-bold rounded-full shadow-md hover:shadow-lg transition-all duration-300 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white ${
          icon ? "px-2.5 py-1.5 text-xs" : compact ? "px-3 py-1.5 text-sm" : "px-4 py-2 text-sm md:text-base"
        }`}
      >
        <Download className="w-4 h-4" />
        <span>{icon ? "App" : "Get App 📲"}</span>
      </button>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-xl">📲 Install TinyMinds</DialogTitle>
            <DialogDescription>Add TinyMinds to your home screen — opens like a real app!</DialogDescription>
          </DialogHeader>
          <ol className="space-y-2 text-sm font-fredoka text-gray-700">
            {platform === "ios" ? (
              <>
                <li>1️⃣ Open this page in <b>Safari</b>.</li>
                <li>2️⃣ Tap the <b>Share</b> button (square with arrow ⬆️).</li>
                <li>3️⃣ Choose <b>Add to Home Screen</b>, then tap <b>Add</b>.</li>
              </>
            ) : platform === "android" ? (
              <>
                <li>1️⃣ Open this page in <b>Chrome</b>.</li>
                <li>2️⃣ Tap the <b>⋮ menu</b> at the top right.</li>
                <li>3️⃣ Tap <b>Install app</b> or <b>Add to Home screen</b>.</li>
              </>
            ) : (
              <>
                <li>1️⃣ Use <b>Chrome</b> or <b>Edge</b>.</li>
                <li>2️⃣ Click the <b>install icon</b> in the address bar.</li>
                <li>3️⃣ Click <b>Install</b>.</li>
              </>
            )}
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
